<template>
    <div>
        <!-- Message d'erreur si popup bloquée -->
        <b-alert v-if="popupBlocked" show variant="warning" dismissible @dismissed="popupBlocked = false">
            <h4 class="alert-heading">
                <b-icon icon="exclamation-triangle-fill"></b-icon>
                Popup bloquée
            </h4>
            <p>
                La fenêtre SCORM n'a pas pu s'ouvrir. Veuillez vérifier que les popups ne sont pas bloquées par votre navigateur et
                réessayer.
            </p>
            <b-button variant="outline-warning" @click="openScormPopup"> Réessayer </b-button>
        </b-alert>

        <!-- Indicateur de chargement -->
        <b-alert v-if="loading" show variant="info">
            <b-spinner small></b-spinner>
            Chargement du contenu SCORM...
        </b-alert>

        <!-- Indicateur de popup ouverte -->
        <b-alert v-if="popupOpen && !popupBlocked" show variant="success" class="d-flex align-items-center justify-content-between">
            <div>
                <b-icon icon="window" class="mr-2"></b-icon>
                <strong>{{ lessonTitle }}</strong> - Fenêtre SCORM ouverte
            </div>
            <b-button variant="outline-success" size="sm" @click="focusPopup"> Retourner à la leçon </b-button>
        </b-alert>

        <!-- Message d'erreur -->
        <b-alert v-if="error" show variant="danger" dismissible @dismissed="error = null">
            <h4 class="alert-heading">Erreur</h4>
            <p>{{ error }}</p>
        </b-alert>
    </div>
</template>

<script>
import { GET_LESSON_SCORM_URL } from '@/core/services/store/api/scorm.service';

export default {
    name: 'ScormPlayer',
    props: {
        show: {
            type: Boolean,
            default: false
        },
        classeUserId: {
            type: Number,
            required: true
        },
        lessonId: {
            type: Number,
            required: true
        },
        lessonTitle: {
            type: String,
            default: 'Leçon SCORM'
        }
    },
    data() {
        return {
            loading: false,
            error: null,
            popupOpen: false,
            popupBlocked: false,
            scormUrl: null
        };
    },
    created() {
        // Créer popupWindow comme une propriété non-réactive
        this._popupWindow = null;
    },
    watch: {
        show: {
            handler(newVal) {
                if (newVal) {
                    this.initializeScorm();
                } else {
                    this.cleanup();
                }
            },
            immediate: true
        }
    },
    beforeDestroy() {
        this.cleanup();
    },
    methods: {
        async initializeScorm() {
            try {
                this.loading = true;
                this.error = null;

                // Récupérer l'URL du SCORM
                await this.getScormUrl();

                // Ouvrir la popup
                this.openScormPopup();
            } catch (error) {
                this.error = error.message || 'Erreur lors du chargement du SCORM';
            } finally {
                this.loading = false;
            }
        },

        async getScormUrl() {
            try {
                const url = await this.$store.dispatch(GET_LESSON_SCORM_URL, this.lessonId);
                this.scormUrl = url;
            } catch (error) {
                throw new Error("Impossible de récupérer l'URL du SCORM");
            }
        },

        openScormPopup() {
            if (!this.scormUrl) {
                this.error = 'URL du SCORM non disponible';
                return;
            }

            // Paramètres de la popup
            const popupFeatures = [
                'width=1200',
                'height=800',
                'scrollbars=yes',
                'resizable=yes',
                'toolbar=no',
                'menubar=no',
                'location=no',
                'directories=no',
                'status=no'
            ].join(',');

            // URL du bridge SCORM via la route Laravel avec authentification web
            const bridgeUrl =
                process.env.VUE_APP_PLATEFORME_URL +
                '/scorm-bridge?' +
                new URLSearchParams({
                    scorm_url: this.scormUrl,
                    lesson_id: this.lessonId,
                    classe_user_id: this.classeUserId
                }).toString();

            // Ouvrir la popup avec le fichier bridge
            this._popupWindow = window.open(bridgeUrl, 'scorm_' + this.lessonId, popupFeatures);

            // Vérifier si la popup a été bloquée
            if (!this._popupWindow || this._popupWindow.closed) {
                this.popupBlocked = true;
                this.popupOpen = false;
                return;
            }

            this.popupOpen = true;
            this.popupBlocked = false;

            // Configurer la communication avec la popup
            this.setupPopupCommunication();
        },

        setupPopupCommunication() {
            // Surveiller la fermeture de la popup
            const checkClosed = setInterval(() => {
                try {
                    if (this._popupWindow && this._popupWindow.closed) {
                        clearInterval(checkClosed);
                        this.handlePopupClosed();
                    }
                } catch (e) {
                    // Si on ne peut pas accéder à la popup, on considère qu'elle est fermée
                    clearInterval(checkClosed);
                    this.handlePopupClosed();
                }
            }, 1000);

            // Écouter les messages de la popup (pour les événements de completion)
            window.addEventListener('message', (event) => {
                if (event.source === this._popupWindow) {
                    // console.log('Message reçu de la popup SCORM:', event.data);

                    // Traiter les messages de completion
                    if (event.data.type === 'scormComplete') {
                        this.handleScormComplete(event.data);
                    }
                }
            });
        },

        handleScormComplete(completionData) {
            // Émettre l'événement vers le parent pour rafraîchir les données
            this.$emit('scorm-complete', completionData);
        },

        handlePopupClosed() {
            this.popupOpen = false;
            this.cleanup();
            this.$emit('update:show', false);
            // Émettre un événement pour notifier que la popup a été fermée
            this.$emit('popup-closed');
        },

        focusPopup() {
            try {
                if (this._popupWindow && !this._popupWindow.closed) {
                    this._popupWindow.focus();
                }
            } catch (e) {
                // console.warn('Impossible de mettre la popup en focus:', e.message);
            }
        },

        cleanup() {
            // Fermer la popup si elle est ouverte
            try {
                if (this._popupWindow && !this._popupWindow.closed) {
                    this._popupWindow.close();
                }
            } catch (e) {
                // console.warn('Impossible de fermer la popup:', e.message);
            }

            // Réinitialiser les états
            this._popupWindow = null;
            this.popupOpen = false;
        }
    }
};
</script>

<style scoped>
.alert {
    margin-bottom: 1rem;
}
</style>
